import { useCallback, useEffect, useRef } from 'react';
import { IntelligentAssignmentSummary } from '../constants';
import { IntelligentPhotoCaptureAttachmentIterationStatus } from '../enums';
export const useDamageCompletionTimer = ({ attachmentIterationInfo, onUpdateIterationDamageCallStatus }) => {
    const damageCompletionTimeoutsRef = useRef(new Map());
    const damageCompletionTriggerAtRef = useRef(new Map());
    const damageCompletionDispatchInFlightRef = useRef(new Set());
    const onUpdateIterationDamageCallStatusRef = useRef(onUpdateIterationDamageCallStatus);
    const clearIterationTimeout = useCallback((iterationId) => {
        const timeoutId = damageCompletionTimeoutsRef.current.get(iterationId);
        if (timeoutId) {
            clearTimeout(timeoutId);
            damageCompletionTimeoutsRef.current.delete(iterationId);
        }
    }, []);
    const clearIterationTracking = useCallback((iterationId) => {
        clearIterationTimeout(iterationId);
        damageCompletionTriggerAtRef.current.delete(iterationId);
        damageCompletionDispatchInFlightRef.current.delete(iterationId);
    }, [clearIterationTimeout]);
    const dispatchCompletion = useCallback((iterationId) => {
        damageCompletionDispatchInFlightRef.current.add(iterationId);
        onUpdateIterationDamageCallStatusRef.current(iterationId, undefined, IntelligentPhotoCaptureAttachmentIterationStatus.COMPLETED);
    }, []);
    const completeImmediatelyIfEligible = useCallback((iterationId) => {
        if (damageCompletionDispatchInFlightRef.current.has(iterationId)) {
            return true;
        }
        clearIterationTimeout(iterationId);
        damageCompletionTriggerAtRef.current.delete(iterationId);
        dispatchCompletion(iterationId);
        return true;
    }, [clearIterationTimeout, dispatchCompletion]);
    const scheduleCompletion = useCallback((iterationId, triggerAtMs) => {
        const existingTriggerAtMs = damageCompletionTriggerAtRef.current.get(iterationId);
        if (existingTriggerAtMs === triggerAtMs && damageCompletionTimeoutsRef.current.has(iterationId)) {
            return;
        }
        clearIterationTimeout(iterationId);
        const remainingMs = triggerAtMs - Date.now();
        const timeoutId = setTimeout(() => {
            if (damageCompletionDispatchInFlightRef.current.has(iterationId)) {
                return;
            }
            damageCompletionTimeoutsRef.current.delete(iterationId);
            damageCompletionTriggerAtRef.current.delete(iterationId);
            dispatchCompletion(iterationId);
        }, remainingMs);
        damageCompletionTimeoutsRef.current.set(iterationId, timeoutId);
        damageCompletionTriggerAtRef.current.set(iterationId, triggerAtMs);
    }, [clearIterationTimeout, dispatchCompletion]);
    const clearAllScheduledTracking = useCallback(() => {
        damageCompletionTimeoutsRef.current.forEach((timeoutId) => {
            clearTimeout(timeoutId);
        });
        damageCompletionTimeoutsRef.current.clear();
        damageCompletionTriggerAtRef.current.clear();
    }, []);
    const clearAllTracking = useCallback(() => {
        clearAllScheduledTracking();
        damageCompletionDispatchInFlightRef.current.clear();
    }, [clearAllScheduledTracking]);
    const syncCompletionForIteration = useCallback((iteration, completeLeadTimeMs) => {
        const { iterationId } = iteration;
        if (iteration.damageCallStatus !== IntelligentPhotoCaptureAttachmentIterationStatus.IN_PROGRESS ||
            typeof iteration.damageCallAt !== 'number' ||
            !Number.isFinite(iteration.damageCallAt)) {
            clearIterationTracking(iterationId);
            return;
        }
        const triggerAtMs = iteration.damageCallAt + completeLeadTimeMs;
        if (triggerAtMs <= Date.now()) {
            completeImmediatelyIfEligible(iterationId);
            return;
        }
        scheduleCompletion(iterationId, triggerAtMs);
    }, [clearIterationTracking, completeImmediatelyIfEligible, scheduleCompletion]);
    useEffect(() => {
        onUpdateIterationDamageCallStatusRef.current = onUpdateIterationDamageCallStatus;
    }, [onUpdateIterationDamageCallStatus]);
    useEffect(() => {
        if (!attachmentIterationInfo.length) {
            clearAllTracking();
            return;
        }
        const completeLeadTimeMs = Math.max(0, IntelligentAssignmentSummary.SEND_ENABLE_DELAY_FROM_LAST_DAMAGE_POST_MS);
        attachmentIterationInfo.forEach((iteration) => {
            syncCompletionForIteration(iteration, completeLeadTimeMs);
        });
        return () => {
            clearAllScheduledTracking();
        };
    }, [attachmentIterationInfo, clearAllScheduledTracking, clearAllTracking, syncCompletionForIteration]);
};
//# sourceMappingURL=useDamageCompletionTimer.js.map